'use client';

import { GripVerticalIcon, XIcon } from 'lucide-react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type FC,
} from 'react';

import { useResizePx } from '../hooks/useResizePx';
import { cn } from '../lib/utils';

type SidePanelChromeContextValue = {
  showCloseButton: boolean;
  onClose: () => void;
  setHeaderHandlesClose: (present: boolean) => void;
};
const SidePanelChromeContext = createContext<SidePanelChromeContextValue | null>(null);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SidePanelSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface SidePanelProps extends React.ComponentProps<'div'> {
  /** Controlled open state. */
  open: boolean;
  /** Called when the panel requests to close (ESC key or close button). */
  onOpenChange?: (open: boolean) => void;
  /** Which edge the panel slides from. @default 'right' */
  side?: 'left' | 'right';
  /** Width preset. @default 'md' */
  size?: SidePanelSize;
  /**
   * Display mode.
   * - `'overlay'` – absolutely positioned on top of sibling content (default).
   * - `'push'` – participates in document flow so sibling content is pushed aside.
   *    The parent must be a flex-row container.
   * @default 'overlay'
   */
  mode?: 'overlay' | 'push';
  /** Show the built-in close button. @default true */
  showCloseButton?: boolean;
  /** Close when the Escape key is pressed. @default true */
  closeOnEscape?: boolean;
  /** Enable drag-to-resize on the panel edge. @default false */
  resizable?: boolean;
  /** Controlled width in pixels. Only used when `resizable` is true. */
  width?: number;
  /** Callback when the user drags to resize. Only used when `resizable` is true. */
  onWidthChange?: (width: number) => void;
  /** Minimum width in pixels when resizable. @default 320 */
  minWidth?: number;
  /** Maximum width in pixels when resizable. @default 960 */
  maxWidth?: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SIZE_CLASSES: Record<SidePanelSize, string> = {
  sm: 'w-3/4 sm:max-w-md',
  md: 'w-3/4 sm:max-w-lg',
  lg: 'w-3/4 sm:max-w-2xl',
  xl: 'w-3/4 sm:max-w-5xl',
  full: 'w-[90vw] sm:max-w-6xl',
};

/** Fixed inner widths for push mode so content doesn't reflow during the width transition. */
const PUSH_INNER_WIDTHS: Record<SidePanelSize, string> = {
  sm: 'min-w-[28rem]',
  md: 'min-w-[32rem]',
  lg: 'min-w-[42rem]',
  xl: 'min-w-[64rem]',
  full: 'min-w-[72rem]',
};

/** Max-width targets for push mode open state. */
const PUSH_MAX_WIDTHS: Record<SidePanelSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-5xl',
  full: 'max-w-6xl',
};

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

const DEFAULT_MIN_WIDTH = 320;
const DEFAULT_MAX_WIDTH = 960;

/**
 * Slide-in/out animation classes shared by the push (inner wrapper) and overlay
 * wrappers so the two modes stay in sync.
 *
 * Enter uses the tailwindcss-animate keyframe: DrilldownModal and
 * TransactionVideoPanel mount already-open, and a transition never animates on
 * mount (no prior computed style to animate from) — only a keyframe does.
 *
 * Exit is a plain transition to the static closed transform, not a keyframe. A
 * freshly (re-)inserted node therefore renders off-screen immediately instead of
 * replaying an exit animation, and an interrupted transition snaps to the
 * declared value rather than a keyframe's `from`. `invisible` on the closed
 * state makes that robust regardless of what triggers a re-insertion; per spec a
 * visibility transition with one visible endpoint stays visible for the whole
 * duration, so the slide-out still plays.
 *
 * Emit exactly ONE transition-property utility here — `transition` and
 * `transition-[...]` have equal specificity, so a second one would win or lose
 * on source order alone.
 */
function slideAnimationClasses(side: 'left' | 'right') {
  return [
    'transition-[transform,visibility] ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:invisible',
    side === 'right' &&
      'data-[state=closed]:translate-x-full data-[state=open]:slide-in-from-right',
    side === 'left' && 'data-[state=closed]:-translate-x-full data-[state=open]:slide-in-from-left',
  ];
}

const CloseButton: FC<{ onClick: () => void; className?: string }> = ({ onClick, className }) => (
  <button
    data-slot="side-panel-close"
    type="button"
    onClick={onClick}
    className={cn(
      'flex size-10 shrink-0 items-center justify-center rounded-xs',
      'text-foreground-primary opacity-100 ring-offset-background transition-opacity',
      'focus:outline-hidden hover:opacity-70 focus:ring-2 focus:ring-ring focus:ring-offset-2',
      '[&>svg]:size-4 [&>svg]:text-current',
      className,
    )}
  >
    <XIcon />
    <span className="sr-only">Close panel</span>
  </button>
);

const ResizeHandle: FC<{
  side: 'left' | 'right';
  resizeRef: React.Ref<HTMLDivElement>;
}> = ({ side, resizeRef }) => {
  return (
    <div
      ref={resizeRef}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panel"
      className={cn(
        'group/resize absolute inset-y-0 z-50 flex w-2 cursor-col-resize items-center justify-center transition-colors hover:bg-primary/10 active:bg-primary/20',
        side === 'right' ? '-left-[5px]' : '-right-[5px]',
      )}
    >
      <div className="flex h-8 w-3 items-center justify-center rounded-sm border border-transparent bg-border text-muted-foreground transition-colors group-hover/resize:bg-primary group-hover/resize:text-primary-foreground group-hover/resize:border-primary/30 group-active/resize:bg-primary/80">
        <GripVerticalIcon size={10} />
      </div>
    </div>
  );
};

export const SidePanel: FC<SidePanelProps> = ({
  open,
  onOpenChange,
  side = 'right',
  size = 'md',
  mode = 'overlay',
  showCloseButton = true,
  closeOnEscape = true,
  resizable = false,
  width,
  onWidthChange,
  minWidth = DEFAULT_MIN_WIDTH,
  maxWidth = DEFAULT_MAX_WIDTH,
  className,
  children,
  ...rest
}) => {
  const requestClose = useCallback(() => onOpenChange?.(false), [onOpenChange]);

  const { resizeRef } = useResizePx({
    width: width ?? 0,
    onWidthChange: onWidthChange ?? (() => {}),
    minWidth,
    maxWidth,
    direction: side === 'right' ? 'left' : 'right',
  });

  const hasResizeWidth = resizable && width != null;
  const resizeStyle = hasResizeWidth ? { width: `${width}px` } : undefined;

  // Push mode + fixed width: release the reserved space by animating the outer
  // wrapper's max-width to 0 on close. The transition needs a concrete open value
  // to animate FROM, so we mirror the pixel width when open. The inner wrapper
  // keeps that width as a `minWidth` (not `min-w-full`) so it doesn't squish as
  // the outer collapses — it slides out under the outer's `overflow-hidden` clip.
  const pushOuterStyle = hasResizeWidth
    ? { width: `${width}px`, maxWidth: open ? `${width}px` : 0 }
    : undefined;
  const pushInnerStyle = hasResizeWidth ? { minWidth: `${width}px` } : undefined;

  // ESC key handler
  useEffect(() => {
    if (!open || !closeOnEscape) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        requestClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeOnEscape, requestClose]);

  const [headerHandlesClose, setHeaderHandlesClose] = useState(false);
  const chrome = useMemo(
    () => ({
      showCloseButton,
      onClose: requestClose,
      setHeaderHandlesClose,
    }),
    [showCloseButton, requestClose],
  );
  const showFallbackClose = showCloseButton && !headerHandlesClose;

  const isPush = mode === 'push';

  if (isPush) {
    // Push mode: the panel participates in document flow so it pushes sibling
    // content aside.  The outer wrapper transitions max-width (0 ↔ target) to
    // reserve/release space, while the inner wrapper uses a translateX slide
    // animation so the visual feel matches overlay mode.
    return (
      <div
        {...rest}
        data-slot="side-panel"
        data-state={open ? 'open' : 'closed'}
        role={open ? 'region' : undefined}
        aria-hidden={!open}
        {...(!open && { inert: '' as unknown as boolean })}
        // `pushOuterStyle` is undefined when not resizable, matching the plain
        // (non-width) push layout.
        style={pushOuterStyle}
        className={cn(
          'tw',
          // `relative` makes this the positioning context for the resize handle.
          'relative flex-shrink-0',
          // `overflow-visible` while open keeps the resize handle (which straddles
          // the edge at -left-[5px]) from being clipped. While closing we switch to
          // `overflow-hidden` so the inner wrapper is clipped as it slides out and
          // the outer collapses — no handle is rendered when closed anyway.
          hasResizeWidth && !open ? 'overflow-hidden' : 'overflow-visible',
          !hasResizeWidth && 'transition-[max-width] ease-in-out',
          !hasResizeWidth &&
            (open ? ['duration-500', PUSH_MAX_WIDTHS[size]] : 'max-w-0 duration-300'),
          // Resizable: max-width is driven by style (open → width px, closed → 0).
          // Only transition it while closing — during an open drag the width updates
          // every frame and a transition would make the handle lag the cursor.
          hasResizeWidth && !open && 'transition-[max-width] duration-300 ease-in-out',
          side === 'right' && open && 'border-l',
          side === 'left' && open && 'order-first border-r',
          className,
        )}
      >
        {/* Rendered on the outer wrapper (overflow-visible while open) so the grip
            isn't clipped. Only when open — a closed push panel collapses its
            max-width and a stray handle would otherwise peek out. */}
        {resizable && open && <ResizeHandle side={side} resizeRef={resizeRef} />}
        <div
          style={pushInnerStyle}
          className={cn(
            'relative flex h-full flex-col overflow-hidden bg-background shadow-lg',
            !hasResizeWidth && PUSH_INNER_WIDTHS[size],
            slideAnimationClasses(side),
          )}
          data-state={open ? 'open' : 'closed'}
        >
          {showFallbackClose && (
            <CloseButton onClick={requestClose} className="absolute right-6 top-4" />
          )}
          <SidePanelChromeContext.Provider value={chrome}>
            {children}
          </SidePanelChromeContext.Provider>
        </div>
      </div>
    );
  }

  // Overlay mode (default): absolutely positioned on top of content.
  return (
    <div
      {...rest}
      data-slot="side-panel"
      data-state={open ? 'open' : 'closed'}
      role={open ? 'region' : undefined}
      aria-hidden={!open}
      // Prevent focus & interaction when the panel is off-screen.
      {...(!open && { inert: '' as unknown as boolean })}
      style={resizeStyle}
      className={cn(
        'tw',
        'absolute inset-y-0 z-40 flex flex-col bg-background shadow-lg',
        side === 'right' && 'right-0 border-l',
        side === 'left' && 'left-0 border-r',
        slideAnimationClasses(side),
        !hasResizeWidth && SIZE_CLASSES[size],
        className,
      )}
    >
      {resizable && <ResizeHandle side={side} resizeRef={resizeRef} />}
      {showFallbackClose && (
        <CloseButton onClick={requestClose} className="absolute right-6 top-4" />
      )}
      <SidePanelChromeContext.Provider value={chrome}>{children}</SidePanelChromeContext.Provider>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

export const SidePanelHeader: FC<React.ComponentProps<'div'>> = ({
  className,
  children,
  ...props
}) => {
  const chrome = useContext(SidePanelChromeContext);
  const showsCloseButton = chrome?.showCloseButton ?? true;

  useLayoutEffect(() => {
    chrome?.setHeaderHandlesClose(showsCloseButton);
    return () => chrome?.setHeaderHandlesClose(false);
  }, [chrome, showsCloseButton]);

  return (
    <div
      data-slot="side-panel-header"
      className={cn(
        'flex items-center justify-between gap-4 px-6 py-4 text-left font-semibold heading-lg',
        className,
      )}
      {...props}
    >
      <div data-slot="side-panel-header-content" className="flex min-w-0 flex-1 flex-col">
        {children}
      </div>
      {showsCloseButton && chrome ? <CloseButton onClick={chrome.onClose} /> : null}
    </div>
  );
};

export const SidePanelTitle: FC<React.ComponentProps<'h2'>> = ({ className, ...props }) => (
  <h2
    data-slot="side-panel-title"
    className={cn('font-semibold text-foreground', className)}
    {...props}
  />
);

export const SidePanelDescription: FC<React.ComponentProps<'p'>> = ({ className, ...props }) => (
  <p
    data-slot="side-panel-description"
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
);

export const SidePanelFooter: FC<React.ComponentProps<'div'>> = ({ className, ...props }) => (
  <div
    data-slot="side-panel-footer"
    className={cn('border-t border-border', className)}
    {...props}
  />
);
